import { SearchIcon } from '@heroicons/react/outline';
import Fuse from 'fuse.js';
import { debounce } from 'lodash';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useCallback, useEffect, useMemo, useState } from 'react';

import clsxm from '@/lib/clsxm';

import {
  PresetCategory,
  presetHasCategory,
  presets,
  visiblePresetCategories,
} from '@/constants/presets';

import Filter from './atoms/Filter';
import FilterItem from './atoms/FilterItem';

export default function Example({ showAll = false, isFree = false }) {
  const [open, setOpen] = useState(showAll ? true : false);
  const { t } = useLocale('tools');
  const [selectedCategory, setCategory] = useState<PresetCategory | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  // Create Fuse instance upfront
  const fuse = useMemo(() => {
    const options = {
      keys: ['title', 'searchKeywords'],
      threshold: 0.3,
      includeScore: true,
      minMatchCharLength: 2,
      shouldSort: true,
      weightedKeys: [
        { name: 'title', weight: 2 },
        { name: 'searchKeywords', weight: 1 },
      ],
    };

    return new Fuse(
      presets.filter((preset) => !preset.hideDashoard),
      options,
    );
  }, []);

  // Debounced search effect
  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    handler();
    return () => {
      handler.cancel();
    };
  }, [searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Memoize the search function
  const searchPresets = useCallback(
    (preset: (typeof presets)[0]) => {
      if (!debouncedSearch) return true;

      const results = fuse.search(debouncedSearch);
      const matchedSlugs = results.map((result) => result.item.slug);
      return matchedSlugs.includes(preset.slug);
    },
    [debouncedSearch, fuse],
  );

  // Memoize filtered presets
  const filteredPresets = useMemo(() => {
    let results = presets
      .filter(
        (preset) =>
          !selectedCategory || presetHasCategory(preset, selectedCategory),
      )
      .filter(searchPresets)
      .filter((preset) => !isFree || preset.isFree);

    if (!showAll) {
      results = results.slice(0, 28);
    }

    return results;
  }, [selectedCategory, searchPresets, isFree, showAll]);

  return (
    <div className='relative overflow-y-hidden bg-white py-16 dark:bg-slate-900 sm:py-24 lg:py-32'>
      <div className='mx-auto max-w-md px-4 text-center sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8'>
        {!isFree && (
          <span className='text-lg font-semibold text-indigo-600'>
            {t('Write better and faster')}
          </span>
        )}
        {!isFree && (
          <p className='mx-auto mt-2 max-w-4xl text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            {t(
              'Improve your copy and spark fresh ideas 10X faster with over 200 proven templates',
            )}
          </p>
        )}

        <div className={!isFree ? 'mt-12' : 'mt-0'}>
          {showAll && !isFree && (
            <div>
              <div className='flex min-w-0 justify-center md:px-8 lg:px-0 xl:col-span-6'>
                <div className='flex w-full max-w-xl items-center justify-center py-4'>
                  <div className='w-full'>
                    <label htmlFor='search' className='sr-only'>
                      {t('Search for templates')}
                    </label>
                    <div className='relative'>
                      <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                        <SearchIcon
                          className='h-5 w-5 text-gray-400'
                          aria-hidden='true'
                        />
                      </div>
                      <input
                        id='search'
                        name='search'
                        className='block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:border-primary-500 focus:text-gray-900 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-0 dark:bg-slate-700/50 dark:text-white sm:text-sm'
                        placeholder={t('Search for templates') + '...'}
                        type='search'
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Filter className='flex justify-center'>
                <FilterItem
                  onClick={() => setCategory(null)}
                  isActive={selectedCategory === null}
                >
                  {t('All')}
                </FilterItem>
                {visiblePresetCategories.map((category) => (
                  <FilterItem
                    key={category}
                    isActive={selectedCategory === category}
                    onClick={() => setCategory(category)}
                  >
                    {t(category)}
                  </FilterItem>
                ))}
              </Filter>
            </div>
          )}
          {filteredPresets.length === 0 ? (
            <div className='flex items-center justify-center py-12'>
              <p className='text-lg text-gray-500'>
                {t(
                  'No templates found for this category or search. Please try a different search.',
                )}
              </p>
            </div>
          ) : (
            <div className='grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3'>
              {filteredPresets
                .filter((preset) => !preset.hideLandingPage)
                .filter(
                  (preset) => (!showAll && !preset.isAbsoluteURL) || showAll,
                )
                .map((preset, presetIdx) => (
                  <div
                    key={presetIdx}
                    className={clsxm(
                      presetIdx > 5 && !open ? 'hidden' : '',
                      presetIdx >= 5 && presetIdx < 8 && !open
                        ? 'sm:block'
                        : '',
                      presetIdx >= 8 && presetIdx < 12 && !open
                        ? 'lg:block'
                        : '',
                      'pt-6',
                    )}
                    style={{ height: '100%' }} // Added style to make the cards the same height
                  >
                    <Link
                      href={
                        !preset.isAbsoluteURL
                          ? `/templates/${preset.slug.replace('/', '')}`
                          : preset.landingPageSlug || preset.slug
                      }
                      passHref
                    >
                      <div className='flow-root h-full rounded-lg bg-gray-50 px-6 pb-8 dark:bg-slate-800'>
                        {' '}
                        {/* Added h-full to make the cards the same height */}
                        <div className='-mt-6'>
                          <div>
                            <span
                              className={clsxm(
                                preset.iconBackground,
                                preset.iconForeground,
                                'inline-flex rounded-lg p-3 dark:bg-slate-700',
                              )}
                            >
                              <preset.icon
                                className='h-6 w-6 '
                                aria-hidden='true'
                              />
                            </span>
                          </div>
                          <p className='mt-8 text-lg font-medium tracking-tight text-gray-900 dark:text-slate-300'>
                            {t(preset.title) || preset.title}
                          </p>
                          <p className='mt-5 text-base text-gray-500 dark:text-slate-500'>
                            {t(preset.description) || preset.description}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              {!open && (
                <div className='pointer-events-none absolute -inset-x-2.5 -bottom-2.5 h-[16.6%] bg-gradient-to-t from-white to-white/25 dark:from-slate-900 dark:to-slate-900/25 sm:h-[33.3%]'></div>
              )}
            </div>
          )}
          {!showAll && (
            <div className='pointer-events-none relative z-10 -mt-5 flex justify-center'>
              <Link
                href='/templates'
                className='pointer-events-auto inline-flex justify-center rounded-lg bg-primary-700 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-500'
              >
                <span>{open ? t('Hide') : t('Show more...')}</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
